import dayjs from 'dayjs';
import 'dayjs/locale/ka';
import { useTranslation } from 'react-i18next';

/**
 * The one place the UI language is translated into the locale identifiers the
 * two date libraries speak. Both mappings live here so a picker and a chart
 * axis can never drift apart (the Sales page used to show US `M/D/Y` while
 * Accounting showed `D/M/Y`, and axis labels stayed English in Georgian mode).
 *
 * `i18n.language` can carry a region suffix (`en-US`) or be something we don't
 * ship, so only the primary subtag is inspected and anything unknown falls back
 * to English.
 */

/** dayjs locale name — `dayjs/locale/ka` is registered by importing this file. */
export const getDayjsLocale = (language: string | undefined): string =>
    language?.split('-')[0] === 'ka' ? 'ka' : 'en';

/**
 * The fallback whose day-first order is guaranteed everywhere. `en-GB` rather
 * than `en-US` on purpose: the app shows `DD/MM/YYYY` on every screen.
 */
const DAY_FIRST_FALLBACK = 'en-GB';

/**
 * Whether a locale actually orders a numeric date day-first *in this runtime*.
 *
 * Asked rather than assumed (BOS-165). `ka-GE` is day-first in every ICU build
 * this was checked against — and the app still rendered `9 / 1 / 2026` under
 * ქარ while English rendered `01 / 09 / 2026`, i.e. month-first with US
 * separators. Whatever the cause — a browser ICU without Georgian date data, a
 * locale the runtime silently substitutes — the symptom is the same: a range
 * whose meaning reverses when a Georgian speaker switches to their own
 * language, on Sales, Shifts, Books, Statements and Activity at once.
 *
 * `Intl` is the same machinery react-aria formats segments with, so if it says
 * day-first here, the picker is day-first too.
 */
const dayFirstByLocale = new Map<string, boolean>();

const ordersDayFirst = (locale: string): boolean => {
    // Memoised per locale (BOS-171): this ran an `Intl.DateTimeFormat`
    // construction and a `formatToParts` on every render of every picker, for
    // an answer that cannot change while the page is open. The map is
    // module-level because the answer is a property of the runtime, not of any
    // component.
    const cached = dayFirstByLocale.get(locale);
    if (cached !== undefined) {
        return cached;
    }

    try {
        const parts = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(new Date(Date.UTC(2026, 8, 1)));
        const order = parts
            .filter((part) => part.type !== 'literal')
            .map((part) => part.type);
        const dayFirst = order.indexOf('day') < order.indexOf('month');
        dayFirstByLocale.set(locale, dayFirst);
        return dayFirst;
    } catch {
        // A runtime that cannot format the locale at all certainly cannot be
        // trusted to order it.
        dayFirstByLocale.set(locale, false);
        return false;
    }
};

/**
 * react-aria / `@internationalized/date` locale for every date picker in the
 * app.
 *
 * Georgian keeps `ka-GE` **when the runtime really orders it day-first**, so
 * the calendar popover stays in Georgian; when it does not, the field falls
 * back to `en-GB`. A month name in the popover reading English is a blemish; a
 * date field that means a different day than it says is a reporting error.
 */
export const getPickerLocale = (language: string | undefined): string => {
    if (language?.split('-')[0] !== 'ka') {
        return DAY_FIRST_FALLBACK;
    }
    return ordersDayFirst('ka-GE') ? 'ka-GE' : DAY_FIRST_FALLBACK;
};

/** Format an ISO timestamp with a dayjs pattern in the given UI language. */
export const formatInLanguage = (
    date: string,
    pattern: string,
    language: string | undefined,
): string => dayjs(date).locale(getDayjsLocale(language)).format(pattern);

/** The active UI language's picker locale, for use inside `I18nProvider`. */
export const usePickerLocale = (): string => {
    const { i18n } = useTranslation();
    return getPickerLocale(i18n.language);
};
